/**
 * Soft Button UI Component
 *
 * Modern "soft button" with rounded/pill/circular shapes, subtle gradients,
 * inner highlights, soft drop shadows, and tactile interaction states.
 * Designed to feel like part of the game world (Candy Crush / Royal Match style).
 */

import Phaser from 'phaser'
import { FontConfig } from '../config/UIConfig'

/** Shape options for soft buttons */
export type SoftButtonShape = 'circle' | 'pill' | 'rounded'

/** Configuration for creating a soft button */
export interface SoftButtonConfig {
  /** Center X position */
  x: number
  /** Center Y position */
  y: number
  /** Width (for pill/rounded) or diameter (for circle) */
  width: number
  /** Height (for pill/rounded); ignored for circle */
  height?: number
  /** Button shape */
  shape?: SoftButtonShape
  /** Base fill color (hex number, e.g. 0xc8618d) */
  color: number
  /** Hover fill color (auto-calculated if omitted) */
  hoverColor?: number
  /** Pressed fill color (auto-calculated if omitted) */
  pressColor?: number
  /** Label text (optional – omit for icon-only) */
  label?: string
  /** Icon text/emoji (optional – rendered centered) */
  icon?: string
  /** Font size for label in pixels */
  fontSize?: number
  /** Font size for icon in pixels */
  iconSize?: number
  /** Alpha of the entire button */
  alpha?: number
  /** Depth/z-index */
  depth?: number
  /** Click / tap callback */
  onClick?: () => void
  /** Whether the button is disabled */
  disabled?: boolean
}

/**
 * Color utilities for the soft button system
 */
function lightenColor(color: number, amount: number): number {
  const r = Math.min(255, ((color >> 16) & 0xff) + amount)
  const g = Math.min(255, ((color >> 8) & 0xff) + amount)
  const b = Math.min(255, (color & 0xff) + amount)
  return (r << 16) | (g << 8) | b
}

function darkenColor(color: number, amount: number): number {
  const r = Math.max(0, ((color >> 16) & 0xff) - amount)
  const g = Math.max(0, ((color >> 8) & 0xff) - amount)
  const b = Math.max(0, (color & 0xff) - amount)
  return (r << 16) | (g << 8) | b
}

/**
 * A single soft button instance.
 * Wraps a Phaser Container with gradient background, highlight, shadow,
 * label/icon text, and hover/press interaction states.
 */

/** Minimum touch target size in pixels (WCAG / Apple HIG guideline: 44pt) */
const MIN_TOUCH_TARGET = 44

/** Default icon width as a fraction of button width */
const ICON_WIDTH_RATIO = 0.55

/** Vertical offset ratio when icon coexists with a label */
const ICON_LABEL_OFFSET = 0.08

/** Circular gradient: controls how much the radius shrinks toward the bottom */
const CIRCLE_GRADIENT_RADIUS_SHRINK = 0.15

/** Circular gradient: vertical offset factor for each step (simulates top-down light) */
const CIRCLE_GRADIENT_Y_SHIFT = 0.05

export class SoftButton {
  public readonly container: Phaser.GameObjects.Container
  private scene: Phaser.Scene
  private config: Required<
    Pick<SoftButtonConfig, 'x' | 'y' | 'width' | 'height' | 'shape' | 'color' | 'hoverColor' | 'pressColor' | 'alpha' | 'depth'>
  > & SoftButtonConfig

  private bgGraphics?: Phaser.GameObjects.Graphics
  private bgCanvas?: Phaser.Textures.CanvasTexture
  private bgImage?: Phaser.GameObjects.Image
  private textureKey?: string
  private static textureCounter = 0
  private shadowGraphics!: Phaser.GameObjects.Graphics
  private highlightGraphics!: Phaser.GameObjects.Graphics
  private hitArea!: Phaser.GameObjects.Rectangle
  private labelText?: Phaser.GameObjects.Text
  private iconText?: Phaser.GameObjects.Text
  private isHovered = false
  private isPressed = false
  private isDisabled = false
  private hoverTween?: Phaser.Tweens.Tween
  private pressTween?: Phaser.Tweens.Tween

  constructor(scene: Phaser.Scene, config: SoftButtonConfig) {
    this.scene = scene
    const shape = config.shape ?? 'pill'
    const height = shape === 'circle' ? config.width : (config.height ?? config.width)

    this.config = {
      ...config,
      height,
      shape,
      hoverColor: config.hoverColor ?? lightenColor(config.color, 25),
      pressColor: config.pressColor ?? darkenColor(config.color, 30),
      alpha: config.alpha ?? 1,
      depth: config.depth ?? 0,
    }

    this.isDisabled = config.disabled ?? false
    this.container = scene.add.container(config.x, config.y)
    this.container.setDepth(this.config.depth)

    this.createShadow()
    this.createBackground(this.config.color)
    this.createHighlight()
    this.createHitArea()
    if (config.icon) this.createIcon()
    if (config.label) this.createLabel()
    if (this.isDisabled) this.applyDisabledState()
  }

  /* ------------------------------------------------------------------ */
  /*  Drawing helpers                                                    */
  /* ------------------------------------------------------------------ */

  /** Draw the outer soft drop shadow */
  private createShadow(): void {
    const { width, height, shape } = this.config
    this.shadowGraphics = this.scene.add.graphics()
    this.shadowGraphics.fillStyle(0x000000, 0.18)
    const radius = this.getRadius(width, height, shape)
    if (shape === 'circle') {
      this.shadowGraphics.fillCircle(0, 3, width / 2 + 2)
    } else {
      this.shadowGraphics.fillRoundedRect(
        -width / 2 - 1, -height / 2 + 2,
        width + 2, height + 2,
        radius
      )
    }
    this.container.add(this.shadowGraphics)
  }

  /** Draw the main body gradient (lighter top → darker bottom) */
  private createBackground(color: number): void {
    const { width, height, shape } = this.config
    const radius = this.getRadius(width, height, shape)
    const topColor = lightenColor(color, 20)
    const bottomColor = darkenColor(color, 15)

    if (shape === 'circle') {
      // Draw concentric fills to approximate gradient
      if (this.bgGraphics) this.bgGraphics.destroy()
      this.bgGraphics = this.scene.add.graphics()
      const r = width / 2
      const steps = 8
      for (let i = steps; i >= 0; i--) {
        const t = i / steps
        const c = this.lerpColor(bottomColor, topColor, t)
        const currentR = r - (r * CIRCLE_GRADIENT_RADIUS_SHRINK * (1 - t))
        this.bgGraphics.fillStyle(c, 1)
        this.bgGraphics.fillCircle(0, r * CIRCLE_GRADIENT_Y_SHIFT * (1 - t), currentR)
      }
      this.container.add(this.bgGraphics)
    } else {
      // Use native Canvas 2D for smooth anti-aliased rounded corners.
      // Canvas clip() with roundRect/arcTo produces hardware-anti-aliased
      // edges, unlike the previous fillRect-scanline approach which created
      // staircase artifacts at the corners.
      if (!this.bgCanvas) {
        this.textureKey = `softbtn_${SoftButton.textureCounter++}`
        const canvas = this.scene.textures.createCanvas(this.textureKey, width, height)
        if (!canvas) return
        this.bgCanvas = canvas
        this.bgImage = this.scene.add.image(0, 0, this.textureKey)
        this.container.add(this.bgImage)
      }

      const ctx = this.bgCanvas.context
      ctx.clearRect(0, 0, width, height)
      ctx.save()

      // Create anti-aliased rounded rect clip path
      ctx.beginPath()
      this.canvasRoundRect(ctx, 0, 0, width, height, radius)
      ctx.clip()

      // Smooth linear gradient fill
      const grad = ctx.createLinearGradient(0, 0, 0, height)
      grad.addColorStop(0, this.hexToCSS(topColor))
      grad.addColorStop(1, this.hexToCSS(bottomColor))
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, width, height)

      ctx.restore()
      this.bgCanvas.refresh()
    }
  }

  /** Inner highlight (gloss effect – top-left light source) */
  private createHighlight(): void {
    this.highlightGraphics = this.scene.add.graphics()

    const { width, height, shape } = this.config
    const radius = this.getRadius(width, height, shape)

    this.highlightGraphics.fillStyle(0xffffff, 0.22)
    if (shape === 'circle') {
      // Top-half elliptical highlight
      this.highlightGraphics.fillEllipse(0, -width * 0.12, width * 0.6, width * 0.32)
    } else {
      // Top strip highlight
      const hlH = height * 0.35
      this.highlightGraphics.fillRoundedRect(
        -width / 2 + 4, -height / 2 + 2,
        width - 8, hlH,
        { tl: radius - 2, tr: radius - 2, bl: 0, br: 0 }
      )
    }
    this.container.add(this.highlightGraphics)
  }

  /** Invisible hit-area rectangle for pointer events */
  private createHitArea(): void {
    const { width, height } = this.config
    const hitW = Math.max(width, MIN_TOUCH_TARGET)
    const hitH = Math.max(height, MIN_TOUCH_TARGET)
    this.hitArea = this.scene.add.rectangle(0, 0, hitW, hitH, 0xffffff, 0)
    this.hitArea.setInteractive({ useHandCursor: true })
    this.container.add(this.hitArea)

    this.hitArea.on('pointerover', this.onPointerOver, this)
    this.hitArea.on('pointerout', this.onPointerOut, this)
    this.hitArea.on('pointerdown', this.onPointerDown, this)
    this.hitArea.on('pointerup', this.onPointerUp, this)
    this.hitArea.on('pointercancel', this.onPointerUp, this)
  }

  private createLabel(): void {
    const fontSize = this.config.fontSize ?? 18
    this.labelText = this.scene.add.text(0, this.config.icon ? this.config.height * ICON_LABEL_OFFSET * 1.5 : 0, this.config.label!, {
      fontFamily: FontConfig.primary,
      fontSize: `${fontSize}px`,
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#00000044',
      strokeThickness: 1,
      align: 'center',
    }).setOrigin(0.5)
    this.container.add(this.labelText)
  }

  private createIcon(): void {
    const iconSize = this.config.iconSize ?? Math.round(this.config.width * ICON_WIDTH_RATIO)
    this.iconText = this.scene.add.text(0, this.config.label ? -this.config.height * ICON_LABEL_OFFSET : 0, this.config.icon!, {
      fontSize: `${iconSize}px`,
      align: 'center',
    }).setOrigin(0.5)
    this.container.add(this.iconText)
  }

  /* ------------------------------------------------------------------ */
  /*  Interaction handlers                                               */
  /* ------------------------------------------------------------------ */

  private onPointerOver(): void {
    if (this.isDisabled) return
    this.isHovered = true
    this.redrawBackground(this.config.hoverColor)
    this.highlightGraphics.setAlpha(0.35) // brighter highlight on hover
    this.hoverTween?.destroy()
    this.hoverTween = this.scene.tweens.add({
      targets: this.container,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 120,
      ease: 'Back.easeOut',
    })
  }

  private onPointerOut(): void {
    if (this.isDisabled) return
    this.isHovered = false
    this.isPressed = false
    this.redrawBackground(this.config.color)
    this.highlightGraphics.setAlpha(1)
    this.shadowGraphics.setAlpha(1)
    this.hoverTween?.destroy()
    this.pressTween?.destroy()
    this.scene.tweens.add({
      targets: this.container,
      scaleX: 1,
      scaleY: 1,
      duration: 100,
      ease: 'Cubic.easeOut',
    })
  }

  private onPointerDown(): void {
    if (this.isDisabled) return
    this.isPressed = true
    this.redrawBackground(this.config.pressColor)
    this.highlightGraphics.setAlpha(0.12) // reduced gloss when pressed
    this.shadowGraphics.setAlpha(0.6)     // reduced shadow (pressed in)
    this.pressTween?.destroy()
    this.pressTween = this.scene.tweens.add({
      targets: this.container,
      scaleX: 0.95,
      scaleY: 0.95,
      duration: 60,
      ease: 'Cubic.easeIn',
    })
  }

  private onPointerUp(): void {
    if (this.isDisabled) return
    if (this.isPressed && this.config.onClick) {
      this.config.onClick()
    }
    this.isPressed = false
    const targetColor = this.isHovered ? this.config.hoverColor : this.config.color
    this.redrawBackground(targetColor)
    this.highlightGraphics.setAlpha(this.isHovered ? 0.35 : 1)
    this.shadowGraphics.setAlpha(1)
    this.pressTween?.destroy()
    this.scene.tweens.add({
      targets: this.container,
      scaleX: this.isHovered ? 1.05 : 1,
      scaleY: this.isHovered ? 1.05 : 1,
      duration: 120,
      ease: 'Back.easeOut',
    })
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /** Programmatically trigger hover (for gamepad navigation highlight) */
  public setHighlighted(on: boolean): void {
    if (on) {
      this.onPointerOver()
    } else {
      this.onPointerOut()
    }
  }

  /** Get the internal hit-area rectangle (for gamepad nav registration) */
  public getHitArea(): Phaser.GameObjects.Rectangle {
    return this.hitArea
  }

  /** Update label text */
  public setLabel(text: string): void {
    this.labelText?.setText(text)
  }

  /** Update icon */
  public setIcon(icon: string): void {
    this.iconText?.setText(icon)
  }

  /** Enable / disable the button */
  public setDisabled(disabled: boolean): void {
    this.isDisabled = disabled
    if (disabled) {
      this.applyDisabledState()
    } else {
      this.container.setAlpha(this.config.alpha)
      this.hitArea.setInteractive({ useHandCursor: true })
    }
  }

  /** Reposition the button */
  public setPosition(x: number, y: number): void {
    this.container.setPosition(x, y)
  }

  /** Set depth */
  public setDepth(depth: number): void {
    this.container.setDepth(depth)
  }

  /** Change the base color and redraw the button */
  public setColor(color: number): void {
    this.config.color = color
    this.config.hoverColor = lightenColor(color, 25)
    this.config.pressColor = darkenColor(color, 30)
    this.redrawBackground(color)
  }

  /** Destroy the button and all its children */
  public destroy(): void {
    this.hoverTween?.destroy()
    this.pressTween?.destroy()
    if (this.textureKey && this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey)
    }
    this.bgCanvas = undefined
    this.bgImage = undefined
    this.container.destroy(true)
  }

  /* ------------------------------------------------------------------ */
  /*  Private helpers                                                    */
  /* ------------------------------------------------------------------ */

  private applyDisabledState(): void {
    this.container.setAlpha(0.45)
    this.hitArea.disableInteractive()
  }

  private redrawBackground(color: number): void {
    this.createBackground(color)
    // Re-add highlight and hit area on top
    this.container.bringToTop(this.highlightGraphics)
    this.container.bringToTop(this.hitArea)
    if (this.iconText) this.container.bringToTop(this.iconText)
    if (this.labelText) this.container.bringToTop(this.labelText)
  }

  private getRadius(w: number, h: number, shape: SoftButtonShape): number {
    if (shape === 'circle') return w / 2
    if (shape === 'pill') return Math.min(w, h) / 2
    return Math.min(w, h) * 0.28 // rounded
  }

  private lerpColor(a: number, b: number, t: number): number {
    const rA = (a >> 16) & 0xff, gA = (a >> 8) & 0xff, bA = a & 0xff
    const rB = (b >> 16) & 0xff, gB = (b >> 8) & 0xff, bB = b & 0xff
    const r = Math.round(rA + (rB - rA) * t)
    const g = Math.round(gA + (gB - gA) * t)
    const bl = Math.round(bA + (bB - bA) * t)
    return (r << 16) | (g << 8) | bl
  }

  /** Convert a hex number (e.g. 0xc8618d) to a CSS color string */
  private hexToCSS(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`
  }

  /** Draw a rounded rectangle path on a Canvas 2D context */
  private canvasRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    w: number, h: number,
    r: number
  ): void {
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
  }
}
